import React, { useEffect, useState, useMemo } from "react";
import { useGraphQL } from "../../../hooks/useGraphQL";
import "./ChroniclesSection.scss";
import PurchaseForm from "./PurchaseForm";
import { useNavigate } from "react-router-dom";
import { GET_ALL_COLLECTIONS, SUBGRAPH_URL } from "../Market/Market";

const MARKET_CHRONICLES =
  process.env.REACT_APP_MARKETPLACE_ADDRESS_V1?.toLowerCase();

async function fetchAllMarketListings() {
  const PAGE = 1000;
  let all = [];
  let skip = 0;
  while (true) {
    const query = `
      query {
        listings(where: { active: true }, first: ${PAGE}, skip: ${skip}) {
          market
          token { collection { id } }
        }
      }
    `;
    const res = await fetch(SUBGRAPH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
    });
    const json = await res.json();
    const batch = json.data?.listings || [];
    all.push(...batch);
    if (batch.length < PAGE) break;
    skip += PAGE;
  }
  return all;
}

export default function ChroniclesSection() {
  const { loading: loadingCollections, data: collectionsData } =
    useGraphQL(GET_ALL_COLLECTIONS);

  const [allListings, setAllListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [listingsError, setListingsError] = useState(null);

  const [showPurchase, setShowPurchase] = useState(false);
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [collectionImages, setCollectionImages] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    setLoadingListings(true);
    fetchAllMarketListings()
      .then(setAllListings)
      .catch((e) => setListingsError(e.message))
      .finally(() => setLoadingListings(false));
  }, []);

  const uniqueCollections = useMemo(() => {
    const allCollections = collectionsData?.collections || [];
    const hasMarketData = allListings.some((l) => l.market != null);

    const listingCounts = new Map();
    for (const l of allListings) {
      if (l.market?.toLowerCase() !== MARKET_CHRONICLES) continue;
      const id = l.token?.collection?.id?.toLowerCase();
      if (id) listingCounts.set(id, (listingCounts.get(id) || 0) + 1);
    }

    if (!hasMarketData) {
      return allCollections.map((col) => ({
        ...col,
        id: col.id.toLowerCase(),
        totalListedQuantity: Number(col.totalListedQuantity || 0),
        listingCount: 0,
      }));
    }

    return allCollections
      .filter((col) => listingCounts.has(col.id.toLowerCase()))
      .map((col) => ({
        ...col,
        id: col.id.toLowerCase(),
        totalListedQuantity: Number(col.totalListedQuantity || 0),
        listingCount: listingCounts.get(col.id.toLowerCase()) || 0,
      }));
  }, [allListings, collectionsData]);
  const gateways = [
    "https://funooni-public.mypinata.cloud/ipfs/",
    "https://ipfs.io/ipfs/",
    "https://cloudflare-ipfs.com/ipfs/",
    "https://gateway.pinata.cloud/ipfs/",
  ];

  const safeImage = (uri, gateway) => {
    if (!uri) return "";

    if (uri.startsWith("ipfs://")) {
      const hash = uri.replace("ipfs://", "");
      return `${gateway}${hash}`;
    }

    return uri;
  };
  const fetchIPFSJson = async (uri) => {
    if (!uri) throw new Error("Invalid IPFS URI");

    let lastError = null;

    for (const gateway of gateways) {
      try {
        const url = safeImage(uri, gateway);

        const res = await fetch(url, {
          cache: "force-cache",
        });

        if (!res.ok) {
          throw new Error(`Gateway failed: ${gateway}`);
        }

        return await res.json();
      } catch (err) {
        console.warn(`❌ ${gateway} failed`);
        lastError = err;
      }
    }

    throw lastError;
  };
  const getSafeImage = async (uri) => {
    if (!uri) return "";

    for (const gateway of gateways) {
      const url = safeImage(uri, gateway);

      try {
        const res = await fetch(url, {
          method: "HEAD",
        });

        if (res.ok) {
          return url;
        }
      } catch {}
    }

    return "";
  };

  useEffect(() => {
    const loadCollectionImages = async () => {
      if (!uniqueCollections.length) return;

      const result = {};

      await Promise.all(
        uniqueCollections.map(async (col) => {
          if (!col.contractURI) return;

          const id = col.id?.toLowerCase();

          try {
            const meta = await fetchIPFSJson(col.contractURI);

            result[id] = await getSafeImage(meta.image);
          } catch (err) {
            console.error("Metadata error", err);
          }
        }),
      );

      setCollectionImages(result);
    };

    loadCollectionImages();
  }, [uniqueCollections]);

  const handleNext = () => setPage((p) => Math.min(p + 1, totalPages));
  const handlePrev = () => setPage((p) => Math.max(p - 1, 1));
  const handleBack = () => setShowPurchase(false);

  const handleViewCollection = (collection) => {
    navigate(
      `/reproduction/collections?country=${collection.name}&collection=${collection.id}`,
    );
  };

  const tableRows = uniqueCollections.map((col) => ({
    id: col.id.toLowerCase(),
    name: col.name,
    image: collectionImages[col.id.toLowerCase()],
    collections: col.listingCount,
    items: col.totalListedQuantity,
  }));

  const loading = loadingListings || loadingCollections;
  const totalPages = Math.ceil(tableRows.length / rowsPerPage) || 1;
  const clampedPage = Math.min(page, totalPages);
  const paginatedRows = tableRows.slice(
    (clampedPage - 1) * rowsPerPage,
    clampedPage * rowsPerPage,
  );

  return (
    <section className="wc" id="WorldChronicles">
      {showPurchase ? (
        <>
          <button className="btn btn-secondary" onClick={handleBack}>
            ⬅ Back
          </button>
          <PurchaseForm selectedListingId={selectedListingId} />
        </>
      ) : (
        <>
          <h2>World Chronicles</h2>
          <p>
            Experience history reborn through restored fragments of our past
          </p>

          {loading && <p>Loading collections...</p>}
          {listingsError && (
            <p style={{ color: "red" }}>Error: {listingsError}</p>
          )}

          <table className="wc__table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Collections</th>
                <th>Items</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => (
                <tr key={row.id} className="tr-country">
                  <td className="td-country">
                    {row.image ? (
                      <img
                        src={row.image}
                        alt="collection"
                        style={{
                          width: "40px",
                          height: "25px",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                      />
                    ) : (
                      "Loading..."
                    )}
                    {row.name}
                  </td>
                  <td>{row.collections}</td>
                  <td>{row.items}</td>
                  <td>
                    <button
                      className="btn btn-view"
                      onClick={() => handleViewCollection(row)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {tableRows.length > 0 && (
            <div className="wc__pagination">
              <div className="items__rows rows">
                Rows per page:
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                </select>
              </div>
              <div className="pages">
                <span>
                  Page {clampedPage} / {totalPages}
                </span>
                <button onClick={handlePrev} disabled={clampedPage === 1}>
                  ‹
                </button>
                <button
                  onClick={handleNext}
                  disabled={clampedPage >= totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
